import React, { useEffect, useState } from 'react';
import PersonPickerModal from 'Components/JdOwnership/PersonPickerModal';
import { getApprovalCandidates } from 'Api/apiService';
import {
  ASSIGN_APPROVER, getApproverOptions, getAssignApproverState, getLockedApprover, isOutOfScopeError,
} from 'Utils/sendAndPublish';

// The shared Send for Approval page (wireframe: the "Send for Approval" box), opened by every Send for
// Approval: the editor's (nothing is stored until Send is clicked here; Cancel goes back to the editor with the
// edits kept), the main page's and the "Publish without a review?" box's. It lists the HR Managers with the
// approver right the server returns for this JD (only those with it in their view scope), never the owner; Send
// stays greyed out until someone is picked, and is disabled with a message when nobody is available. Laid out by
// PersonPickerModal, as the delegate and share pickers.
//   open       — shown or not
//   jobId      — the JD (its job_id), so the server lists only approvers who have it in their view scope
//   jdTitle    — the JD's title, in bold in the body
//   ownerEmail — the JD's owner, never offered
//   sending    — the send is in flight
//   onCancel() — close (back to where it was opened from)
//   onSend(approverEmail)
//   reassignedApproverEmail — a review an HR Admin reassigned: pre-set to that approver and locked (part 5)
const AssignApproverModal = ({
  open, jobId, jdTitle, ownerEmail, sending, onCancel, onSend, reassignedApproverEmail = null,
}) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [outOfScope, setOutOfScope] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSelected(null);
    setLoading(true);
    setLoadFailed(false);
    setOutOfScope(false);
    getApprovalCandidates(jobId)
      .then((data) => {
        if (cancelled) return;
        const loaded = getApproverOptions(data, ownerEmail);
        setOptions(loaded);
        const locked = getLockedApprover(loaded, reassignedApproverEmail);
        if (locked) setSelected(locked.selected);
      })
      .catch((err) => {
        if (!cancelled) {
          setOptions([]);
          setLoadFailed(true);
          setOutOfScope(isOutOfScopeError(err));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, jobId, ownerEmail, reassignedApproverEmail]);

  const locked = loading ? null : getLockedApprover(options, reassignedApproverEmail);
  const state = getAssignApproverState({ options, selected, loading, loadFailed, outOfScope });
  const canSend = state.canSend && !locked?.unavailable;
  const message = state.message || locked?.note || null;

  return (
    <PersonPickerModal
      open={open}
      title={ASSIGN_APPROVER.title}
      description={<>{ASSIGN_APPROVER.bodyBefore}<b>{jdTitle}</b>{ASSIGN_APPROVER.bodyAfter}</>}
      label={ASSIGN_APPROVER.approverLabel}
      placeholder={ASSIGN_APPROVER.placeholder}
      options={options}
      selected={selected}
      onSelect={setSelected}
      loading={loading}
      message={message}
      messageTestId="assign-approver-message"
      busy={sending}
      locked={!!locked}
      cancelLabel={ASSIGN_APPROVER.cancelLabel}
      confirmLabel={ASSIGN_APPROVER.sendLabel}
      canConfirm={canSend}
      onCancel={onCancel}
      onConfirm={() => onSend(selected)}
    />
  );
};

export default AssignApproverModal;
